/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { first } from 'rxjs/operators';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { TableCellComponent } from '@src/app/shared/table/table-cell/table-cell.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormComponent } from '@src/app/shared/form/form.component';
import { NgTemplateOutlet } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
export interface DialogOptions {
    config: FormConfig;
    service: AbstractHttpService;
    id?: uuid;
    forceTeamId?: uuid;
    width?: number;
    height?: number;
    showSearch?: boolean;
    addOnly?: boolean;
    hideTabs?: boolean,
    condensed?:boolean
    /** Narrows the candidate list within the team, e.g. only the contacts of one unit */
    listParms?: HttpParams;
    /** The rows to choose from, when the caller already holds them. The service is not read. */
    items?: AbstractObject[];
    prompt?: string;
}
@Component({
    selector: 'app-pick-dialog',
    templateUrl: './pick-dialog.component.html',
    styleUrls: ['./pick-dialog.component.scss'],
    imports: [MatIconModule, MatDialogContent, MatTabsModule, NgTemplateOutlet,
        FormComponent, MatTableModule, MatTooltipModule,
        TableCellComponent, DialogBannerComponent]
})
export class PickDialogComponent implements OnInit, AfterViewInit {
    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
    protected isNarrowSvc = inject(IsNarrowService);
    dialogRef = inject<MatDialogRef<PickDialogComponent>>(MatDialogRef);
    dialogOptions = inject<DialogOptions>(MAT_DIALOG_DATA);


    /*
    config: FormConfig = {
      fields:[
        Field.make.id(),
        Field.make.rev(),
        FormTextComponent.make('Title', 'title'),
        FormTextComponent.make('First Name', 'firstName'),
        FormTextComponent.make('Last Name', 'lastName'),
        FormTextComponent.make('Telephone', 'phone'),
        FormTextComponent.make('Email', 'email'),
        Field.make.notes(),
      ],
      title: $localize `People`,
      service: this.dataSvc,
      mode: 'new',
      objectFactory: () => new Person(),
      formLayout: FormComponent.formLayouts.singleCol
    };
  */
    config: FormConfig;
    columns: Field[] = [];
    columnNames: string[] = [];

    dataSource: MatTableDataSource<AbstractObject>;
    dataItems: AbstractObject[];
    height = 300;
    width = 500;
    showSearch = true;
    addOnly = false;
    prompt = '';

    displayLimit = 100;
    matchCount = 0;
    limited = false;

    // Signals (via data-form-ready in the template) that the picklist has finished loading its rows,
    // so the Playwright framework can wait for it like any other form. The table variant of this
    // dialog has no embedded <app-form> to stamp the attribute, so we drive it from here.
    ready = false;

    constructor() {
        const dialogOptions = this.dialogOptions;

        this.config = dialogOptions.config;
        this.config.service = dialogOptions.service;
        this.showSearch = dialogOptions.showSearch;
        this.prompt = dialogOptions.prompt ?? '';
        if (dialogOptions.width) {
            this.width = this.isNarrowSvc.screenWidth * dialogOptions.width / 100
        } else {
            this.width = this.isNarrowSvc.screenWidth * .80;
        }
        if (dialogOptions.height) {
            this.height = this.isNarrowSvc.screenHeight * dialogOptions.height / 100;
        } else {
            this.height = this.isNarrowSvc.screenHeight * .75;
        }
        if (dialogOptions.addOnly) {
            this.addOnly = dialogOptions.addOnly;
        } else if (dialogOptions.items) {
            this.showItems(dialogOptions.items);
        } else {
            const listParms = dialogOptions.listParms ?? null;
            dialogOptions.service.get(false, listParms, dialogOptions.forceTeamId).pipe(first())
                .subscribe(dataSet => this.showItems(dataSet));
        }
        this.dialogRef.disableClose = true;
    }

    private showItems(items: AbstractObject[]) {
        this.dataItems = items;
        this.dataSource = new MatTableDataSource<AbstractObject>();
        this.updateDisplay('');
        this.ready = true;
    }

    filter(search: string) {
        this.updateDisplay(search);
    }

    private updateDisplay(search: string) {
        const term = search.trim().toLocaleLowerCase();
        const matches = term
            ? this.dataItems.filter(item => this.matchesTerm(item, term))
            : this.dataItems;
        this.matchCount = matches.length;
        this.limited = matches.length > this.displayLimit;
        this.dataSource.data = matches.slice(0, this.displayLimit);
    }

    private matchesTerm(item: AbstractObject, term: string): boolean {
        // item[field.name] only works for a plain top-level property. Columns backed by
        // calculateValue or a dotted path (e.g. 'unit.name') need Field.getValue() to resolve the
        // same way the column itself displays - otherwise search misses those columns entirely.
        return this.columns.some(field => {
            if (!field.visible.computer && !field.visible.phone) return false;
            const value = field.getValue(item);
            return value != null && String(value).toLocaleLowerCase().includes(term);
        });
    }

    ngOnInit(): void {

        this.columns = [];
        this.columnNames = [];

        this.config.fieldSet.fields.forEach(field => {
            this.columns.push(field);
            if (field.visible.computer || field.visible.phone) {
                this.columnNames.push(field.name);
            }
        });
    }

    ngAfterViewInit(): void {
        // Focus the search box once the dialog has settled so the user can type immediately.
        setTimeout(() => this.searchInput?.nativeElement.focus());
    }

    /*
     * A link column - a ref that opens the record in its own tab so it can be checked before it is
     * picked - is not a way of picking the row. The anchor still navigates; it just does not also
     * close the dialog with that row, which would otherwise carry the caller's flow on over
     * whatever page the link went to.
     */
    selectItem(item: AbstractObject, event?: Event) {
        if ((event?.target as HTMLElement)?.closest('a')) {
            return;
        }
        this.dialogRef.close(item);
    }

    closeDialog() {
        this.dialogRef.close(null);
    }
}
